/**
 * Tiền bo cho thợ (KH-GĐ2 5.2, công tắc "Tiền bo"):
 *   - `PhanTienBo`: ô trong tờ thu tiền — số bo, thợ nhận (mặc định thợ của lịch), cách khách đưa, chia đều / nhập tay;
 *   - `TheTienBoNgay`: Sổ tiền → Hôm nay — bo từng thợ + "tiệm còn nợ" + nút "Đã trả" (tiền mặt từ két).
 * Bo không phải tiền của tiệm: không vào tổng hoá đơn, hoa hồng, điểm (máy chủ giữ luật này).
 */
import { HandCoins } from 'lucide-react'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { type Api, ApiFailure, type HinhThucThu, type ThoBo, tienVnd } from './api.ts'
import { useChiNhanhCuaTo } from './chi-nhanh-dang-dung.tsx'
import { OTien, useKhoaIdem } from './GoiVi.tsx'
import { B } from './text/tien-bo.ts'
import { T } from './text/vi.ts'
import { type BoNhap, boHopLe } from './tien-bo.ts'
import { ThongBao, useLanTaiMoiNhat } from './ui.tsx'

const CACH_DUA: { ma: HinhThucThu; ten: string }[] = [
  { ma: 'cash', ten: B.tienMatTay },
  { ma: 'transfer', ten: B.chuyenKhoan },
  { ma: 'card', ten: B.the },
]

export function PhanTienBo({
  tho,
  gt,
  onDoi,
}: {
  tho: { id: string; ten: string }[]
  gt: BoNhap
  onDoi: (b: BoNhap) => void
}) {
  if (!gt.bat) {
    return (
      <button
        type="button"
        className="nut nut-vien nut-nho"
        disabled={tho.length === 0}
        title={tho.length === 0 ? B.chuaCoTho : undefined}
        onClick={() =>
          onDoi({
            ...gt,
            bat: true,
            thoIds: gt.thoIds.length > 0 ? gt.thoIds : tho.map((t) => t.id),
          })
        }
      >
        <HandCoins size={16} />
        {B.mo}
      </button>
    )
  }
  const chon = tho.filter((t) => gt.thoIds.includes(t.id))
  return (
    <div className="muc-to tien-bo">
      <h3>{B.tieuDe}</h3>
      <p className="chu-phu">{B.giaiThich}</p>
      <label className="truong" htmlFor="bo-so-tien">
        <span className="nhan">{B.soTien}</span>
        <OTien id="bo-so-tien" giaTri={gt.soTien} onDoi={(so) => onDoi({ ...gt, soTien: so })} />
      </label>
      {tho.length > 1 && (
        <div className="truong">
          <span className="nhan">{B.thoNhan}</span>
          <div className="hang-chip">
            {tho.map((t) => {
              const co = gt.thoIds.includes(t.id)
              return (
                <button
                  key={t.id}
                  type="button"
                  aria-pressed={co}
                  className={`chip ${co ? 'dang-chon' : ''}`}
                  onClick={() =>
                    onDoi({
                      ...gt,
                      thoIds: co ? gt.thoIds.filter((x) => x !== t.id) : [...gt.thoIds, t.id],
                    })
                  }
                >
                  {t.ten}
                </button>
              )
            })}
          </div>
        </div>
      )}
      {chon.length > 1 && gt.nhapTay && (
        <div className="luoi-chia">
          {chon.map((t) => (
            <label key={t.id} className="truong" htmlFor={`bo-${t.id}`}>
              <span className="nhan">{t.ten}</span>
              <OTien
                id={`bo-${t.id}`}
                giaTri={gt.phan[t.id] ?? ''}
                onDoi={(so) => onDoi({ ...gt, phan: { ...gt.phan, [t.id]: so } })}
              />
            </label>
          ))}
        </div>
      )}
      {chon.length > 1 && (
        <p className="chu-phu">
          {gt.nhapTay ? (boHopLe(gt) ? '' : B.lechTong) : B.chiaDeu(chon.length)}{' '}
          <button
            type="button"
            className="nut-lien-ket"
            onClick={() => onDoi({ ...gt, nhapTay: !gt.nhapTay })}
          >
            {gt.nhapTay ? B.chiaDeuLai : B.nhapTay}
          </button>
        </p>
      )}
      <div className="truong">
        <span className="nhan">{B.cachDua}</span>
        <div className="hang-chip">
          {CACH_DUA.map((c) => (
            <button
              key={c.ma}
              type="button"
              aria-pressed={gt.hinhThuc === c.ma}
              className={`chip ${gt.hinhThuc === c.ma ? 'dang-chon' : ''}`}
              onClick={() => onDoi({ ...gt, hinhThuc: c.ma })}
            >
              {c.ten}
            </button>
          ))}
        </div>
        {gt.hinhThuc !== 'cash' && <p className="chu-phu">{B.tiemNo}</p>}
      </div>
    </div>
  )
}

export function TheTienBoNgay({ api, ngay, choDao }: { api: Api; ngay: string; choDao: boolean }) {
  const [ds, setDs] = useState<ThoBo[] | null>(null)
  // Két nào đưa tiền cho thợ — tiệm nhiều chi nhánh phải chọn đúng (money-auditor 5.2 B1). ADR-101: mặc định = chi nhánh đang dùng.
  const { ds: chiNhanh, branchId, setBranchId } = useChiNhanhCuaTo()
  const [dang, setDang] = useState<string | null>(null)
  const [hoiDao, setHoiDao] = useState<{ id: string; soTienVnd: string; ten: string } | null>(null)
  const [bao, setBao] = useState<string | null>(null)
  const [loi, setLoi] = useState<string | null>(null)
  const [khoa, lamMoiKhoa] = useKhoaIdem()
  useEffect(() => {
    if (hoiDao !== null)
      document.getElementById('hoi-tra-nham')?.scrollIntoView({ block: 'center' })
  }, [hoiDao])

  const lanMoi = useLanTaiMoiNhat()
  const nap = useCallback(() => {
    const conMoi = lanMoi()
    void api
      .tienBo(ngay)
      .then((r) => {
        if (conMoi()) setDs(r.thoBo)
      })
      .catch((e: unknown) => {
        if (conMoi()) setLoi(e instanceof ApiFailure ? e.messageVi : T.serverFailed)
      })
  }, [api, ngay, lanMoi])
  useEffect(() => {
    nap()
  }, [nap])

  async function tra(t: ThoBo) {
    if (branchId === '') return
    setDang(t.staffId)
    setLoi(null)
    try {
      await api.traBo({ staffId: t.staffId, branchId, soTienVnd: t.dangNoVnd }, khoa)
      lamMoiKhoa()
      setBao(B.daTra(t.ten, tienVnd(t.dangNoVnd)))
      nap()
    } catch (e) {
      setLoi(e instanceof ApiFailure ? e.messageVi : T.serverFailed)
    } finally {
      setDang(null)
    }
  }

  async function boLanTra(id: string) {
    setDang(id)
    setLoi(null)
    try {
      await api.daoTraBo(id, B.traNhamLyDo, khoa)
      lamMoiKhoa()
      setHoiDao(null)
      setBao(B.daBoLanTra)
      nap()
    } catch (e) {
      setLoi(e instanceof ApiFailure ? e.messageVi : T.serverFailed)
    } finally {
      setDang(null)
    }
  }

  if (ds === null || ds.length === 0) return null
  const soTien = (s: string) => (s === '0' ? '' : tienVnd(s))
  return (
    <section className="the the-dem so-to">
      <span className="nhan-muc">{B.soTitle}</span>
      <span className="chu-phu">{B.soPhu}</span>
      {bao !== null && (
        <ThongBao loai="thanh-cong" onDong={() => setBao(null)}>
          {bao}
        </ThongBao>
      )}
      {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
      {chiNhanh.length > 1 && ds.some((t) => BigInt(t.dangNoVnd) > 0n) && (
        <label className="truong" htmlFor="bo-chi-nhanh">
          <span className="nhan">{B.chiNhanhTra}</span>
          <select
            id="bo-chi-nhanh"
            className="o-chon"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
          >
            {chiNhanh.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <ul className="ds-so ds-tien-bo">
        {ds.map((t) => {
          const no = BigInt(t.dangNoVnd)
          return (
            <Fragment key={t.staffId}>
              <li>
                <span>
                  <b>{t.ten}</b>{' '}
                  <span className="chu-phu">
                    {B.dongTho(soTien(t.tienMatVnd), soTien(t.chuyenKhoanVnd), soTien(t.theVnd))}
                  </span>
                  <span
                    className={`chu-phu ${no > 0n ? 'thieu-tien' : ''}`}
                    style={{ display: 'block' }}
                  >
                    {no > 0n
                      ? B.conNo(tienVnd(t.dangNoVnd))
                      : no < 0n
                        ? B.thoNoLai(tienVnd((-no).toString()))
                        : B.daTraDu}
                  </span>
                  {t.traHomNay.map((l) => (
                    <span key={l.id} className="chu-phu" style={{ display: 'block' }}>
                      {B.daTraLan(tienVnd(l.soTienVnd))}
                      {l.daDao && ` · ${B.daSuaTra}`}
                      {choDao && !l.daDao && (
                        <>
                          {' · '}
                          <button
                            type="button"
                            className="nut-lien-ket"
                            disabled={dang !== null}
                            onClick={() => setHoiDao({ ...l, ten: t.ten })}
                          >
                            {B.traNham}
                          </button>
                        </>
                      )}
                    </span>
                  ))}
                </span>
                {no > 0n && branchId !== '' && (
                  <button
                    type="button"
                    className="nut nut-vien nut-nho"
                    disabled={dang !== null}
                    onClick={() => void tra(t)}
                  >
                    {B.traNut(tienVnd(t.dangNoVnd))}
                  </button>
                )}
              </li>
              {/* Hỏi lại NGAY dưới dòng thợ vừa chạm, có tên thợ (ui-critic v30). */}
              {hoiDao !== null && t.traHomNay.some((l) => l.id === hoiDao.id) && (
                <li id="hoi-tra-nham" style={{ display: 'block' }}>
                  <ThongBao loai="canh-bao">
                    {B.traNhamHoi(hoiDao.ten, tienVnd(hoiDao.soTienVnd))}
                  </ThongBao>
                  <div className="hang-chip" style={{ marginTop: 12 }}>
                    <button type="button" className="nut nut-vien" onClick={() => setHoiDao(null)}>
                      {B.thoi}
                    </button>
                    <button
                      type="button"
                      className="nut nut-nguy-dac"
                      style={{ flex: 1 }}
                      disabled={dang !== null}
                      onClick={() => void boLanTra(hoiDao.id)}
                    >
                      {B.boLanTra}
                    </button>
                  </div>
                </li>
              )}
            </Fragment>
          )
        })}
      </ul>
    </section>
  )
}
